import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { InfiniteQueryConfig } from '@/src/lib/react-query/react-query';
import { SortOrder } from '@/src/util/filters';

import { MyPortfolioColumnsEnum } from '../../MyPortfolio/types';
import { Task } from '../../MyPortfolio/types/task';
import { TASKS_RQ_KEY } from '@/src/components/tasks/api/config';

const url = `${PREFIX_URL}/tasks/assigned/get`;

export const LIST_LIMIT = 20;

export type RequestFilter = {
  field: MyPortfolioColumnsEnum;
  conditions: { op: string; val: string | { from: string; to: string } | string[] | boolean }[];
};

export type RequestFilterInput = RequestFilter[] | RequestFilter[][];

export type RequestSort = {
  field: MyPortfolioColumnsEnum;
  order: SortOrder;
};

export type MyTasksResponse = {
  page: number;
  limit: number;
  count: number;
  tasks: Task[];
};

type getMyTasksDTO = {
  page?: number;
  limit?: number;
  filter?: RequestFilterInput;
  sort?: RequestSort;
  taskIds?: number[];
};

export const getMyTasks = (params: getMyTasksDTO): Promise<MyTasksResponse> => {
  return axios.post(url, {
    page: params.page,
    limit: params.limit,
    filter: params.filter,
    sort: params.sort,
  });
};

export const myPortfolioTaskKeys = {
  list: [TASKS_RQ_KEY, 'MyTasks'] as const,
};

type QueryFnType = typeof getMyTasks;

type useMyTasksOptions = {
  limit?: number;
  filter?: RequestFilterInput;
  sort?: RequestSort;
  config?: InfiniteQueryConfig<QueryFnType>;
};

export const useMyTasks = ({
  filter,
  limit = LIST_LIMIT,
  config,
  ...otherParams
}: useMyTasksOptions) => {
  const queryInfo = useInfiniteQuery({
    queryKey: [...myPortfolioTaskKeys.list, filter, limit, otherParams],
    queryFn: ({ pageParam = 1 }) => getMyTasks({ page: pageParam, filter, limit, ...otherParams }),
    cacheTime: 0,
    getNextPageParam: (lastPage, allPages) => {
      // Assuming lastPageData is an array of objects and totalCount is available
      const currentPageCount = allPages.length * lastPage.limit;

      if (currentPageCount < lastPage.count) {
        // If there are more items to fetch, return the next page number
        return allPages.length + 1;
      }

      // If all items have been fetched, return undefined to stop fetching
      return undefined;
    },
    ...config,
  });

  return {
    queryInfo,
    count: queryInfo.data?.pages[0]?.count,
    limit: queryInfo.data?.pages[0]?.limit,
    data: React.useMemo(
      () => queryInfo.data?.pages.flatMap((page) => page.tasks),
      [queryInfo.data]
    ),
  };
};
